import React, { CSSProperties } from 'react';
import { observer } from 'mobx-react-lite';
import { PillUnit } from '@mark43/cad-api';
import { Tooltip } from './tooltip';
import { getColorForHeldUnit, unitStatusColors } from '../helpers/styles';
import {
    BoxProps,
    VStack,
    Box,
    ContextMenu,
    ContextMenuContent,
    ContextMenuTrigger,
    Text,
} from '@cad-arc';
import type { ContextMenuWriteContextValue } from '../../queues/context/context-menu-state-context';
import { UnitContextMenu } from '../../unit-context-menu/unit-context-menu';
import { formatHoursMinutesSeconds } from '../helpers/utils';
import { useContextMenuStateRead } from '../../queues/context/use-context-menu-state';
import { useEnabledFeatures } from '../hooks/enabled-features/use-enabled-features';

function useIsContextMenuOpenForUnit(unitId: number) {
    const content = useContextMenuStateRead();
    const isUnitContextMenuOpen = content?.kind === 'UNIT' && content.entityId === unitId;
    const isHeldUnitContextMenuOpen = content?.kind === 'HELD_UNIT' && content.data.id === unitId;
    return isUnitContextMenuOpen || isHeldUnitContextMenuOpen;
}

export const UnitPill = observer(function UnitPill({
    unit,
    contextMenuStateWrite,
    enableContextMenu,
    style = {},
    variant = 'default',
}: {
    unit: PillUnit;
    enableContextMenu?: boolean;
    contextMenuStateWrite?: ContextMenuWriteContextValue;
    style?: CSSProperties;
    variant?: 'default' | 'ghost';
}): React.ReactElement {

    const isContextMenuOpen = useIsContextMenuOpenForUnit(unit.id);
    const { staffCountEnabled } = useEnabledFeatures();

    const selectedUnitColors = {
        fill: 'var(--arc-colors-selected-default)',
        outline: 'var(--arc-colors-selected-default)',
        text: 'var(--arc-colors-selected-content)',
    };

    const ghostUnitColors = {
        fill: '',
        outline: 'var(--arc-colors-text-tertiary)',
        text: 'var(--arc-colors-text-tertiary)',
    };

    const heldColors = getColorForHeldUnit(unit);

    const heldUnitColors = {
        fill: '',
        outline: heldColors.outline,
        text: heldColors.text,
    };

    const unitColors = {
        ...unitStatusColors[unit.unitStateGroup ?? 'NO_UNIT_STATE_GROUP'],
    };

    let pillColors: { fill: string; outline?: string; text: string };

    if (isContextMenuOpen) {
        pillColors = selectedUnitColors;
    } else if (variant === 'ghost') {
        pillColors = ghostUnitColors;
    } else if (unit.isHeld) {
        pillColors = heldUnitColors;
    } else {
        pillColors = unitColors;
    }

    const Pill = (
        <Box
            display="flex"
            alignItems="center"
            h="21px"
            w="fit-content"
            padding="0px 4px 0px 3px"
            border={pillColors.outline ? `1px ${variant === 'ghost' ? 'dashed' : 'solid'} ${pillColors.outline}` : undefined}
            borderRadius="2"
            cursor="default"
            userSelect="none"
            fontFamily="jaf-facitweb, sans-serif"
            backgroundColor={pillColors.fill}
            fontSize="13px"
            className="unit-pill"
            {...(style as BoxProps)}
        >
            <Text
                data-testid={'unit-' + unit.callSign}
                casing="uppercase"
                fontWeight="bold"
                className="unit-pill-text"
                style={{ color: pillColors.text, whiteSpace: 'nowrap' }}
            >
                {unit.callSign}
            </Text>
        </Box>
    );

    const tooltipSubtitle = [
        unit.unitStateDisplayValue,
        unit.unitStatusUpdatedDateUtc
            ? formatHoursMinutesSeconds(unit.unitStatusUpdatedDateUtc)
            : undefined,
    ]
        .filter(Boolean)
        .join(', ');

    const PillWithMaybeTip =
        unit.tip || tooltipSubtitle ? (
            <Tooltip
                overlay={
                    <VStack align="start" spacing={1}>
                        {staffCountEnabled && (
                            <Text
                                as="span"
                                fontSize="sm"
                                fontWeight="bold"
                                color="primary"
                                style={{ marginBottom: 'var(--arc-space-2)' }}
                            >
                                Staff Count: {unit.staffCount ?? 0}
                            </Text>
                        )}
                        {unit.tip &&
                            unit.tip.split('/').map((person) => <Text key={person}>{person}</Text>)}
                        {!!tooltipSubtitle && (
                            <Text
                                style={{
                                    marginTop: 'var(--arc-space-2)',
                                }}
                                fontSize="sm"
                                color="tertiary"
                                variant="caption"
                            >
                                {tooltipSubtitle}
                            </Text>
                        )}
                    </VStack>
                }
            >
                {Pill}
            </Tooltip>
        ) : (
            Pill
        );
    if (enableContextMenu && contextMenuStateWrite) {
        return (
            <div
                onContextMenu={() =>
                    contextMenuStateWrite(
                        unit.isHeld
                            ? { kind: 'HELD_UNIT', data: unit }
                            : { kind: 'UNIT', entityId: unit.id }
                    )
                }
            >
                {PillWithMaybeTip}
            </div>
        );
    }
    return enableContextMenu ? (
        <ContextMenu>
            <ContextMenuTrigger asChild>{PillWithMaybeTip}</ContextMenuTrigger>
            <ContextMenuContent>
                <UnitContextMenu unitId={unit.id} />
            </ContextMenuContent>
        </ContextMenu>
    ) : (
        PillWithMaybeTip
    );
});
